import { createPortal } from 'react-dom';
import JxForm from '../../JxControls/JxForms/JxForm';
import calendarEngine from '../Core/CalendarEngine';
import calEventEditEngine from '../Core/CalEventEditEngine';
import calendarUpdator from '../Core/CalendarUpdater';
import CalendarCard from './CalendarCard';

const ANIM_MS = 180;

/**
 * CalendarCard2 — floating CalEvent card, extends JxForm.
 * zIndex + drag + bringToTop via JxForm chain (JxFormBase/JxFormDraggableBase).
 * minimize/dismiss/editReminder roepen engines direct aan — geen onX props.
 * @param {object} props
 * @param {CalEventObj} props.event
 * @param {number} props.index - used for default position offset
 * @param {boolean} props.open
 * @param {function} props.onClose - called by JxWindowManager on closeWindow
 */
export default class CalendarCard2 extends JxForm {
    constructor(props) {
        super(props);
        this.name = 'CalendarCard2';
        this.schema = null;

        this.state = {
            ...this.state,
            animating: false,
            transform: 'none',
            opacity: 1,
        };
    }

    // Per-instance stagger (props.index) so multiple cards cascade near the top-right
    // corner instead of overlapping — legitimate viewport-anchor, not centering-magic.
    getDefaultGeo() {
        const { index = 0 } = this.props;
        return {
            top: 16 + index * 20,
            left: window.innerWidth - 168 - index * 20,
            width: 148,
            height: 200,
        };
    }

    /**
     * Drag end — persists final pos to BE after JxFormDraggableBase finishes its own bookkeeping
     * (cursor/body reset, dragging=false, publishWindowPos). Fires once per drag, not per mousemove.
     */
    handleMouseUp() {
        super.handleMouseUp();

        const { geo } = this.state;
        this.logDebug('handleMouseUp', 'patch event');
        calendarUpdator.patchCalEvent(this.props.event, { pos: { top: geo.top, left: geo.left } });
    }

    /**
     * Escape → minimize (not close/destroy).
     * @param {number} action
     */
    handleClose = (action) => {
        try {
            this._handleMinimize();
        } catch (error) {
            this.logError('handleClose', error.message);
        }
    };

    /**
     * Animates card toward the calendar anchor, then calls calendarEngine.minimize.
     */
    _handleMinimize = () => {
        const cardEl = document.getElementById(this.getId());
        const anchor = document.getElementById('calendar-anchor');

        if (!anchor || !cardEl) {
            calendarEngine.minimize(this.props.event);
            return;
        }

        const aRect = anchor.getBoundingClientRect();
        const cRect = cardEl.getBoundingClientRect();

        const dx = aRect.left + aRect.width / 2 - (cRect.left + cRect.width / 2);
        const dy = aRect.top + aRect.height / 2 - (cRect.top + cRect.height / 2);

        this.setState({
            animating: true,
            transform: `translate(${dx}px, ${dy}px) scale(0.05)`,
            opacity: 0,
        });

        setTimeout(() => calendarEngine.minimize(this.props.event), ANIM_MS);
    };

    /**
     * Dismisses the event via calendarEngine.
     */
    _handleDismiss = async () => {
        try {
            await calendarEngine.dismiss(this.props.event);
        } catch (error) {
            this.logError('_handleDismiss', error.message);
        }
    };

    /**
     * Opens the reminder editor for this event via calEventEditEngine.
     */
    _handleEditReminder = () => {
        calEventEditEngine.startEdit(this.props.event.rk);
    };

    render() {
        if (!this.props.open) return null;

        const { event } = this.props;
        const { animating, transform, opacity } = this.state;

        const panelStyle = {
            position: 'fixed',
            ...this.panelStyle,
            transform,
            opacity,
            transition: animating ? `transform ${ANIM_MS}ms ease-in, opacity ${ANIM_MS}ms ease-in` : 'none',
            pointerEvents: 'auto',
        };

        return createPortal(
            <div {...this.panelProps} style={panelStyle} className={this.panelActiveClass}>
                <CalendarCard
                    event={event}
                    dragHandleProps={this.dragHandleProps}
                    ringing={event.isRinging()}
                    selected={this.state.isActive}
                    onMinimize={this._handleMinimize}
                    onDismiss={this._handleDismiss}
                    onEditReminder={this._handleEditReminder}
                />
            </div>,
            document.body,
        );
    }
}
